import { Box, Typography, useTheme, Tooltip } from '@mui/material';
import { type GridColumns, type GridValidRowModel } from '@mui/x-data-grid';
import { CustomDataGrid, DialogBox } from 'components';
import { CurrencyFormater } from 'components/helpers/commonFunctions/CurrencyFormater';
import { strings } from 'i18n/Strings';
import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { viewMonthlyFeeCalculationData, fetchMonthlyFundUnits } from 'service';
import { colorTheme } from 'theme';
import {
  setCurrentPage,
  setSortBy,
  setSortOrder,
} from 'store/component/pagination';
import { checkSortingValues, dateFormat } from 'utils/GeneralUtil';
import { commonConstantTexts } from 'constants/constantText';
import MonthYearPicker from './MonthYearPicker';

interface IDailyFeeProps {
  monthlyFeeId: string;
  fund1MonthlyFee: string;
  fund2MonthlyFee: string;
  fund3MonthlyFee: string;
}

interface MonthlyUnitProps {
  fundId: number;
  month: string;
  year: string;
  page: number;
  size: number;
  sortBy: string;
  sortOrder: string;
  deductionType?: string;
}

const MonthlyFeeCalculation = (): JSX.Element => {
  const theme = useTheme();
  const colors = colorTheme(theme.palette.mode);
  const [feeData, setFeeData] = useState<IDailyFeeProps[]>([]);
  const [pageCount, setPageCount] = useState(null);
  const dispatch = useDispatch();
  const [openModal, setOpenModal] = useState(false);
  const [modalData, setModalData] = useState<MonthlyUnitProps[]>([]);
  const [modalPageCount, setModalPageCount] = useState(null);
  const [modalRow, setModalRow] = useState<GridValidRowModel | null>(null);
  const pageInfo = useSelector((state) => state?.pagination?.monthlyFee);
  type GridRowData = Record<string, unknown>;

  const modalPageInfo = useSelector(
    (state) => state?.pagination?.monthlyFeeUnitsModal,
  );

  const handleOpenModal = (row: GridValidRowModel): void => {
    setModalRow(row);
    setOpenModal(true);
  };

  const handleCloseModal = (): void => {
    setOpenModal(false);
    setModalData([]);
  };

  const columns: GridColumns<GridValidRowModel> = [
    {
      field: 'date',
      flex: 1,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          Date
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {dateFormat(row.day)}
        </Typography>
      ),
    },
    {
      field: 'fundId',
      flex: 1,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.fundId}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.fundId}
        </Typography>
      ),
    },
    {
      field: 'deductionType',
      flex: 1,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.type}
        </Typography>
      ),
      renderCell: ({ row }) => {
        const type = row.deductionType;

        const label =
          type === commonConstantTexts.withdrawalLabel
            ? commonConstantTexts.withdrawalLabel
            : type === commonConstantTexts.transferOutLabel
            ? commonConstantTexts.transferOutLabel
            : commonConstantTexts.monthlyChargeLable;

        return (
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {label}
          </Typography>
        );
      },
    },
    {
      field: 'fundName',
      flex: 3,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          Fund Name
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={13}>
          {row?.fund?.description}
        </Typography>
      ),
    },
    {
      field: 'monthlyFee',
      flex: 1,
      cellClassName: 'name-column--cell',
      headerAlign: 'right',
      align: 'right',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          Monthly Fee
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {CurrencyFormater(Number(row?.totalMonthlyFee))}
        </Typography>
      ),
    },
    {
      field: 'units',
      flex: 1,
      cellClassName: 'name-column--cell',
      headerAlign: 'right',
      align: 'right',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          Units
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          <a
            style={{ color: 'blue', textDecoration: 'underline' }}
            onClick={() => {
              handleOpenModal(row);
            }}
          >
            {Number(row?.totalUnits)}
          </a>
        </Typography>
      ),
    },
  ];

  const modalColumns: GridColumns<GridValidRowModel> = [
    {
      field: 'date',
      headerName: 'Date',
      flex: 1,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.dateColumn}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {dateFormat(row.createdAt)}
        </Typography>
      ),
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1.5,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.employeeName}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.user?.firstName ?? ''} {row?.user?.lastName ?? ''}
        </Typography>
      ),
    },
    {
      field: 'fundName',
      headerName: 'Fund Name',
      flex: 2.5,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.fundName}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Tooltip title={row.fund.description} placement="top">
          <Typography
            variant="h5"
            color={colors.gray[100]}
            fontSize={14}
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '95%',
              display: 'block',
            }}
          >
            {row?.fund?.description ?? '-'}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'monthlyFee',
      flex: 1,
      cellClassName: 'name-column--cell',
      headerAlign: 'right',
      align: 'right',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          Value Deducted
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {CurrencyFormater(Number(row?.monthlyFee))}
        </Typography>
      ),
    },
    {
      field: 'monthlyUserFundUnits',
      headerName: 'Units',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          Units Deducted
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {Number(row?.monthlyUserFundUnits)}
        </Typography>
      ),
    },
  ];

  const updateFeeData = (): void => {
    viewMonthlyFeeCalculationData(
      Number(pageInfo?.page) + 1,
      pageInfo?.pageSize,
    )
      .then((monthlyFeeData) => {
        if (monthlyFeeData) {
          const basicInfo = monthlyFeeData as {
            basicInfo: IDailyFeeProps[];
          };
          if (basicInfo) {
            const monthlyFeeInfo = basicInfo?.rows?.map((entry, index) => {
              return { ...entry, id: index };
            });
            setFeeData(monthlyFeeInfo);
            setPageCount(basicInfo?.count);
          }
        }
      })
      .catch((err: Error) => {
        throw err;
      });
  };

  useEffect(() => {
    updateFeeData();
  }, [pageInfo?.page, pageInfo?.pageSize]);

  useEffect(() => {
    const fetchModalData = async (): Promise<void> => {
      if (!modalRow) return;
      const createdAtDate = new Date(modalRow.day || modalRow.createdAt);
      const dayValue = createdAtDate.getDate();
      const res = await fetchMonthlyFundUnits({
        day: dayValue,
        fundId: modalRow.fundId ?? modalRow.fund?.id,
        month: modalRow.month ?? modalRow.month,
        year: modalRow.year ?? modalRow.year,
        page: Number(modalPageInfo?.page) + 1,
        size: modalPageInfo?.pageSize,
        sortBy: modalPageInfo?.sortBy,
        sortOrder: modalPageInfo?.sortOrder,
        deductionType: modalRow.deductionType,
      });
      setModalData(
        (res?.rows ?? []).map((row, index) => ({
          ...row,
          id: index,
        })),
      );
      setModalPageCount(res?.count);
      // console.log('modalRow:', modalRow);
      // console.log('API response:', res?.data?.rows);
    };

    if (openModal) void fetchModalData();
  }, [
    openModal,
    modalPageInfo?.page,
    modalPageInfo?.pageSize,
    modalPageInfo?.sortBy,
    modalPageInfo?.sortOrder,
  ]);

  return (
    <Box>
      <MonthYearPicker />
      <CustomDataGrid
        columns={columns}
        rows={feeData}
        getRowId={(row: GridRowData) => row.id}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
        }
        currentPage="monthlyFee"
        pageCount={pageCount}
      />
      {openModal && (
        <DialogBox
          open={openModal}
          onClose={() => {
            handleCloseModal();
          }}
          title="Units"
          PaperProps={{
            sx: {
              width: '100%',
              maxWidth: '1000px',
              height: '90%',
            },
          }}
        >
          <Box>
            <Box mt={-4} padding={1}>
              <CustomDataGrid
                columns={modalColumns}
                rows={modalData}
                getRowId={(row: GridRowData) => row.id}
                getRowClassName={(params) =>
                  params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
                }
                pageCount={modalPageCount}
                currentPage="monthlyFeeUnitsModal"
                sortModel={checkSortingValues(modalPageInfo)}
                onSortModelChange={(mode) => {
                  dispatch(
                    setCurrentPage({ key: 'monthlyFeeUnitsModal', value: 0 }),
                  );
                  dispatch(
                    setSortBy({
                      key: 'monthlyFeeUnitsModal',
                      value: mode[0]?.field ?? '',
                    }),
                  );
                  dispatch(
                    setSortOrder({
                      key: 'monthlyFeeUnitsModal',
                      value: mode[0]?.sort ?? 'asc',
                    }),
                  );
                }}
                autoHeight={false}
              />
            </Box>
          </Box>
        </DialogBox>
      )}
    </Box>
  );
};

export default MonthlyFeeCalculation;
